import * as React from "react";
import { Check, ShieldAlert, X } from "lucide-react";
import { DigestChip } from "./digest-chip";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export interface ApprovalPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  actionDigest: string;
  revision: number;
  whatChanged: React.ReactNode;
  isAuthor: boolean;
  requesterName?: string;
  isPending?: boolean;
  onApprove?: (reason?: string) => void;
  onReject?: (reason: string) => void;
}

/**
 * ApprovalPanel handles decision gates bound to immutable action digests and revisions.
 * Crucial governance invariant: The decision buttons are strictly ABSENT (never disabled)
 * for the author of the change.
 */
export function ApprovalPanel({
  title,
  actionDigest,
  revision,
  whatChanged,
  isAuthor,
  requesterName: _requesterName,
  isPending = false,
  onApprove,
  onReject,
  className,
  ...props
}: ApprovalPanelProps) {
  const [feedback, setFeedback] = React.useState("");
  const [rejectionError, setRejectionError] = React.useState<string | null>(null);

  function handleApprove() {
    setRejectionError(null);
    onApprove?.(feedback.trim() ? feedback.trim() : undefined);
  }

  function handleReject() {
    if (!feedback.trim()) {
      setRejectionError("A rejection requires a reason.");
      return;
    }
    setRejectionError(null);
    onReject?.(feedback.trim());
  }

  return (
    <Card className={cn("overflow-hidden", className)} {...props}>
      <CardHeader>
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-base font-semibold">{title}</CardTitle>
          <span className="text-xs text-muted-foreground">
            Revision <span className="font-mono font-medium">r{revision}</span>
          </span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Action digest:</span>
          <DigestChip value={actionDigest} label="action digest" />
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-4 text-sm">
        {/* Verification note */}
        <div className="rounded-lg border border-border/80 bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
          Bound to action digest{" "}
          <span className="font-mono text-foreground">{actionDigest.slice(0, 16)}…</span> and
          revision <span className="font-mono text-foreground">r{revision}</span>. If the digest or
          revision moves before you decide, the decision is refused.
        </div>

        {/* What changed section */}
        <div className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            What changed
          </h3>
          <div className="overflow-x-auto rounded-md border border-border bg-muted/30 p-3 font-mono text-xs">
            {whatChanged}
          </div>
        </div>

        {/* Governance rule: author cannot approve own changes */}
        {isAuthor ? (
          <div
            role="status"
            className="flex items-start gap-2.5 rounded-lg border border-info/35 bg-info/10 p-3 text-xs text-info"
          >
            <ShieldAlert aria-hidden className="mt-0.5 size-4 shrink-0" />
            <div className="flex flex-col gap-0.5">
              <p className="font-medium">You are the author of this change</p>
              <p className="text-muted-foreground">
                In line with project governance rules, authors cannot decide their own approvals.
                Another reviewer or owner must decide this.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3 pt-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="approval-feedback" className="text-xs font-medium text-foreground">
                Feedback note{" "}
                <span className="font-normal text-muted-foreground">
                  (required to reject, optional for approval)
                </span>
              </label>
              <Textarea
                id="approval-feedback"
                value={feedback}
                onChange={(e) => {
                  setFeedback(e.target.value);
                  if (rejectionError) setRejectionError(null);
                }}
                placeholder="Notes or requirements for the author…"
                rows={2}
                aria-invalid={Boolean(rejectionError)}
                aria-describedby={rejectionError ? "approval-feedback-error" : undefined}
              />
              {rejectionError ? (
                <p id="approval-feedback-error" role="alert" className="text-xs text-danger">
                  {rejectionError}
                </p>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                onClick={handleApprove}
                disabled={isPending}
                className="gap-1.5"
              >
                <Check aria-hidden className="size-4" />
                {isPending ? "Approving…" : `Approve rev ${revision}`}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleReject}
                disabled={isPending}
                className="gap-1.5"
              >
                <X aria-hidden className="size-4" />
                {isPending ? "Rejecting…" : "Reject"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
